import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { authAPI } from '../utils/api';
import { toast } from 'sonner';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Zadejte platný e-mail');
      return;
    }
    setLoading(true);
    try {
      await authAPI.forgotPassword(email.trim().toLowerCase());
      setSent(true);
    } catch (err) {
      // Backend returns 200 even for unknown emails – any error here is a real problem
      toast.error(err.response?.data?.detail || 'Chyba při odesílání. Zkuste to prosím znovu.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center py-16 px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 border border-orange-100" data-testid="forgot-password-card">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-orange-600 mb-4">
            <ArrowLeft className="w-4 h-4" /> Zpět na přihlášení
          </Link>

          {sent ? (
            <div className="text-center py-6" data-testid="forgot-password-success">
              <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2">Zkontrolujte e-mail</h1>
              <p className="text-gray-600 mb-4">
                Pokud existuje účet s e-mailem <strong>{email}</strong>, odeslali jsme na něj odkaz pro obnovení hesla. Platí <strong>24 hodin</strong>.
              </p>
              <p className="text-sm text-gray-500">
                Nedorazil? Zkontrolujte složku SPAM nebo požádejte o nový odkaz.
              </p>
              <Button
                variant="outline"
                onClick={() => { setSent(false); setEmail(''); }}
                className="mt-6"
                data-testid="forgot-password-again"
              >
                Odeslat znovu
              </Button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold mb-2">Zapomněli jste heslo?</h1>
              <p className="text-gray-600 mb-6 text-sm">
                Zadejte e-mail, se kterým jste se registrovali. Pošleme vám odkaz pro nastavení nového hesla.
              </p>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="fp-email">E-mail</Label>
                  <div className="relative mt-1">
                    <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      id="fp-email"
                      type="email"
                      placeholder="vas@email.cz"
                      className="pl-10"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="forgot-password-email-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-6"
                  data-testid="forgot-password-submit"
                >
                  {loading ? 'Odesílání…' : 'Odeslat odkaz pro reset'}
                </Button>
              </form>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
